import { TGroupedGetAllAvailablePrizesResponse } from '@daymade/api-contracts';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { prizeTableByPlanType } from 'components/how-it-works/prizeTableData';
import MainLayout from 'components/layout/MainLayout';
import PageTitle from 'components/PageTitle';
import { MainDrawScheduleCode, MainDrawTypeCode } from 'constants/Draw';
import { PRIZE_TIERS } from 'constants/LottoPrizeValues';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import { useEffect, useMemo, useState } from 'react';
import {
  EecProduct,
  getProductListTrackingName,
  trackEecImpression,
} from 'utils/analytics/analytics';
import { tryApi } from 'utils/api/common';
import { prizeApiClient } from 'utils/apiClient';
import { ASSET_PREFIX, PRIZE_NUMBERS } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import styles from './AllPrizesByTier.module.css';
import { prizePageIndexMetadata } from './AllPrizesPageShared';
import PrizeCategoryCarousel from './PrizeCategoryCarousel';
import { SearchFilter } from './SearchFilter';
import SubmitPrizeSection2026 from 'components/SubmitPrizeSection2026';
import RoundedButton from 'components/RoundedButton';

const AllPrizesByTier = observer(() => {
  const [loading, setLoading] = useState(false);
  const [groupedPrizes, setGroupedPrizes] =
    useState<TGroupedGetAllAvailablePrizesResponse>();
  const userStore = useUserStore();
  const tierForMatches =
    userStore.subscriptionStatusInfo.subscriberStatus || 'PREMIUM';

  useEffect(() => {
    trackEecImpression(getProductListTrackingName(''), EecProduct.LOTTERY);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const result = await tryApi(
        prizeApiClient.public.getGroupedPrizes({
          query: {
            drawTypeCode: MainDrawTypeCode,
            drawScheduleCode: MainDrawScheduleCode.TUESDAY,
            count: 8,
            groupBy: 'tierCode',
          },
        }),
      );
      if (result.status === HttpStatusCode.OK) {
        setGroupedPrizes(result.body);
      } else {
        throw result.error;
      }
      setLoading(false);
    })();
  }, []);

  const groupedPrizesWithTierInfo = useMemo(() => {
    if (!groupedPrizes) {
      return undefined;
    }
    const orderOfTiers = Object.values(PRIZE_TIERS);

    const result = orderOfTiers.map((tierInfo) => {
      const criteria = prizeTableByPlanType[tierForMatches].prizes.find(
        (x) => tierInfo.key == x.prizeTier.key,
      );
      return {
        tier: tierInfo,
        prizes: groupedPrizes[tierInfo.key] || [],
        criteria,
      };
    });
    return result;
  }, [groupedPrizes, tierForMatches]);

  return (
    <MainLayout additionalClassName="prizesPage" limitItemWidths>
      <NextSeo
        title={prizePageIndexMetadata.metaTitle}
        description={prizePageIndexMetadata.metaDescription}
        openGraph={{
          title: prizePageIndexMetadata.metaTitle,
          description: prizePageIndexMetadata.metaDescription,
        }}
      />

      <div className="aboutUsTypographyReset">
        <PageTitle
          rebrandDm2025
          heroBackgroundImage={`${ASSET_PREFIX}/images/prizes/hero-background.svg`}
        >
          Prizes
        </PageTitle>
        <AboutUsModule background="grey" withReducedPadding>
          <AboutUsSiteContainer>
            <SearchFilter
              filterPlaceholder="Categories"
              filterType="category"
            />
            <p className={styles.introParagraph}>
              Win {PRIZE_NUMBERS.PREMIUM} epic prizes from beauty boxes to Bali
              brunch. Explore them all below!
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="white">
          <div className={styles.groupingContainer}>
            {groupedPrizesWithTierInfo &&
              groupedPrizesWithTierInfo.map((x, i) => {
                return (
                  <PrizeCategoryCarousel
                    key={i}
                    categoryTitle={
                      <>
                        {x.tier.title}
                        {x.tier.subtitle && <em>{x.tier.subtitle}</em>}
                      </>
                    }
                    categorySubtitle={'Prizes ' + x.tier.examplePrizes}
                    image={<img src={x.tier.badge} alt="" />}
                    prizes={x.prizes}
                    showPrizeTierBadge={false}
                    groupingPageHref={'/prizes/?tier=' + x.tier.key}
                    groupingImageSrc={`https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2025/how-it-works/desktop/${x.tier.key}.jpg`}
                    criteria={x.criteria}
                  />
                );
              })}
          </div>
          <div className={styles.backToTopContainer}>
            <RoundedButton href="#" rebrandDm2025 colorStyle={'OutlineDark'}>
              Return to top
            </RoundedButton>
          </div>
        </AboutUsModule>
        <SubmitPrizeSection2026 />
      </div>
    </MainLayout>
  );
});

export default AllPrizesByTier;
